import React, { useState, useEffect } from 'react';
import { Button, Modal, Form, Input, InputNumber, DatePicker, Select, Alert, Typography, Tooltip, Tag, Spin } from 'antd';
import { CrownOutlined, InfoCircleOutlined, ArrowUpOutlined, ArrowDownOutlined, PlusOutlined, MinusOutlined } from '@ant-design/icons';
import SelectSearchInput from '../../utils/components/selectSearchInput';
import moment from 'moment'
import axios from '../../services/axios'
import { ITINERAIRES_LIST } from '../../utils/constants/pays';

const { Option } = Select;
const { Text } = Typography;

interface Values {
  title: string;
  description: string;
  modifier: string;
}

interface VipBalance {
  solde: number;
  devise: string;
  client_id: number;
  type_solde: 'credit_client' | 'dette_client' | 'equilibre';
}

interface CollectionCreateFormProps {
  visible: boolean;
  onCreate: (values: Values) => void;
  onCancel: () => void;
  defaultVal?: any;
  clients: any[];
  devises: any[];
  loading?: boolean;
  setClientVisible: () => void;

  isVipClient?: boolean;
  vipBalance?: VipBalance | null;
  vipBalanceLoading?: boolean;
  onClientChange?: (clientId: number) => void;
  onDeviseEntreeChange?: (deviseCode: string) => void;
}

const CollectionCreateForms: React.FC<CollectionCreateFormProps> = ({
                                                                      visible,
                                                                      onCreate,
                                                                      onCancel,
                                                                      clients,
                                                                      devises,
                                                                      defaultVal,
                                                                      loading,
                                                                      setClientVisible,
                                                                      isVipClient = false,
                                                                      vipBalance = null,
                                                                      vipBalanceLoading = false,
                                                                      onClientChange,
                                                                      onDeviseEntreeChange,
                                                                    }) => {
  const [form] = Form.useForm();
  const [canEnterMontant, setCanEnterMontant] = useState(false);
  const [shouldRecalculate, setShouldRecalculate] = useState(false);
  const [suppSign, setSuppSign] = useState<1 | -1>(1);
  const [suppAbsValue, setSuppAbsValue] = useState<number>(0);


  useEffect(() => {
    if (shouldRecalculate) {
      const montant      = form.getFieldValue('montant');
      const deviseId     = form.getFieldValue('devise_id');
      const deviseSortie = form.getFieldValue('devise_sortie');
      const taux         = form.getFieldValue('taux');
      if (montant && deviseId && deviseSortie && taux) {
        getAmountConvert();
      }
      setShouldRecalculate(false);
    }
  }, [shouldRecalculate]);

  useEffect(() => {
    if (!visible) {
      form.setFieldsValue({ montant_supplementaire: 0 });
      setSuppSign(1);
      setSuppAbsValue(0);
    }
  }, [visible]);

  const onReset = () => {
    form.resetFields();
    setCanEnterMontant(false);
    setSuppSign(1);
  }

  const onChange = (name: string, value: any) => {
    form.setFieldsValue({ [name]: value });
  }

  const onSearch = (val: any) => {}

  const checkConversionFields = () => {
    const deviseId     = form.getFieldValue('devise_id');
    const deviseSortie = form.getFieldValue('devise_sortie');
    const taux         = form.getFieldValue('taux');
    if (deviseId && deviseSortie && taux) {
      setCanEnterMontant(true);
      return true;
    }
    return false;
  }

  const onDeviseChange = (name: string, value: any) => {
    onChange(name, value);
    if (name === 'devise_id' && onDeviseEntreeChange) {
      onDeviseEntreeChange(value);
    }
    setTimeout(() => {
      checkConversionFields();
      const montant = form.getFieldValue('montant');
      if (montant) setShouldRecalculate(true);
    }, 100);
  }

  const onTauxChange = (value: any) => {
    if (value) {
      checkConversionFields();
      const montant = form.getFieldValue('montant');
      if (montant && form.getFieldValue('devise_id') && form.getFieldValue('devise_sortie')) {
        setShouldRecalculate(true);
      }
    } else {
      setCanEnterMontant(false);
      form.setFieldsValue({ 'montant': null, 'montant_convert': null });
    }
  }

  const onMontantChange = (value: any) => {
    if (!value) {
      form.setFieldsValue({ 'montant_convert': null });
      return;
    }
    if (form.getFieldValue('devise_id') && form.getFieldValue('devise_sortie') && form.getFieldValue('taux')) {
      const supp = Number(form.getFieldValue('montant_supplementaire')) || 0;
      getAmountConvert(Number(value) + supp);
    }
  }

  const getAmountConvert = (montantOverride?: number) => {
    const montant = montantOverride ?? form.getFieldValue('montant');
    const data = {
      "devise_id":     form.getFieldValue('devise_id'),
      "devise_sortie": form.getFieldValue('devise_sortie'),
      "montant":       montant,
      "taux":          form.getFieldValue('taux'),
    };
    axios.post(`operations/devises/convert`, data)
        .then((response) => {
          form.setFieldsValue({ 'montant_convert': response.data });
        })
        .catch((err) => console.log(err));
  }

  const onMontantSupplementaireChange = (value: any) => {
    const montant = form.getFieldValue('montant');
    if (!montant) return;
    const supp         = Number(value) || 0;
    const montantTotal = Number(montant) + supp;
    if (form.getFieldValue('devise_id') && form.getFieldValue('devise_sortie') && form.getFieldValue('taux')) {
      getAmountConvert(montantTotal);
    }
  }

  // Appelé quand on change le signe via les boutons
  const handleSignChange = (newSign: 1 | -1) => {
    setSuppSign(newSign);
    const newVal = suppAbsValue * newSign;
    form.setFieldsValue({ montant_supplementaire: newVal });
    onMontantSupplementaireChange(newVal);
  }

  const renderVipBalancePanel = () => {
    if (isVipClient && vipBalanceLoading) {
      return (
          <div style={{ textAlign: 'center', padding: '8px 0', marginBottom: 12 }}>
            <Spin size="small" />
            <Text type="secondary" style={{ marginLeft: 8 }}>Vérification du solde VIP...</Text>
          </div>
      );
    }

    if (!isVipClient || !vipBalance) return null;

    const solde        = vipBalance.solde;
    const devise       = vipBalance.devise;
    const soldeFormate = Math.abs(solde).toFixed().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

    if (solde === 0) {
      return (
          <Alert
              style={{ marginBottom: 12 }}
              message={
                <span>
              <CrownOutlined style={{ color: '#faad14', marginRight: 6 }} />
              <Text strong>Client VIP</Text>
                  {' — '}
                  <Text type="secondary">Aucun solde en attente. Transaction normale.</Text>
            </span>
              }
              type="info"
              showIcon={false}
          />
      );
    }

    if (solde > 0) {
      return (
          <Alert
              style={{ marginBottom: 12 }}
              message={
                <span>
              <CrownOutlined style={{ color: '#faad14', marginRight: 6 }} />
              <Text strong>Client VIP</Text>
                  {' — Crédit dû au client : '}
                  <Tag color="green" icon={<ArrowUpOutlined />}>
                +{soldeFormate} {devise}
              </Tag>
              <br />
              <Text type="secondary" style={{ fontSize: 11 }}>
                Ce montant sera automatiquement ajouté à la conversion.
                Le client récupérera plus que ce qu'il dépose.
              </Text>
            </span>
              }
              type="success"
              showIcon={false}
          />
      );
    }

    return (
        <Alert
            style={{ marginBottom: 12 }}
            message={
              <span>
            <Text strong>Client VIP</Text>
                {' — Dette du client : '}
                <Tag color="red" icon={<ArrowDownOutlined />}>
              -{soldeFormate} {devise}
            </Tag>
            <br />
            <Text type="secondary" style={{ fontSize: 11 }}>
              Ce montant sera automatiquement déduit de la conversion.
              Le client récupérera moins que ce qu'il dépose.
            </Text>
          </span>
            }
            type="warning"
            showIcon={false}
        />
    );
  };

  return (
      <Modal
          visible={visible}
          title={
            <>
              Effectuer un nouveau dépôt
              {isVipClient && (
                  <Tag color="gold" icon={<CrownOutlined />} style={{ marginLeft: 10, fontSize: 12 }}>
                    Client VIP
                  </Tag>
              )}
              <Button onClick={() => setClientVisible()} type="dashed" style={{ marginLeft: 8 }}>
                Nouveau Client
              </Button>
            </>
          }
          okText="Enregistrer"
          cancelText="annuler"
          onCancel={onCancel}
          confirmLoading={loading}
          onOk={() => {
            form
                .validateFields()
                .then(values => {
                  form.resetFields();
                  setCanEnterMontant(false);
                  setSuppSign(1);
                  onCreate(values);
                })
                .catch(info => console.log('Validate Failed:', info));
          }}
      >
        <Form
            autoComplete='on'
            form={form}
            layout="horizontal"
            name="form_in_modal"
            initialValues={{
              'type_paiement':          'espece',
              'motif':                  'achat',
              'date_operation':         moment(),
              'taux':                   1,
              'montant_supplementaire': 0,
            }}
        >
          <Form.Item label="Date dépôt" name="date_operation" rules={[{ required: true }]}>
            <DatePicker format="YYYY-MM-DD" placeholder="date" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name='client_id' label="Clients" rules={[{ required: true }]}>
            <SelectSearchInput
                onChange={(v) => {
                  onChange('client_id', v);
                  if (onClientChange) onClientChange(v);
                }}
                data={clients}
                label='prenom'
                label2='nom'
                label3='telephone'
                valueLabel='id'
                onSearch={(value) => onSearch(value)}
                placeholder="Sélectionnez le client"
            />
          </Form.Item>

          {renderVipBalancePanel()}

          <Form.Item label="Déposant" style={{ marginBottom: 0 }}>
            <Form.Item name='nom_deposant' style={{ display: 'inline-block', width: 'calc(50% - 8px)' }}>
              <Input placeholder="nom déposant" autoComplete='on' />
            </Form.Item>
            <Form.Item name='tel_deposant' style={{ display: 'inline-block', width: 'calc(50% - 8px)', margin: '0 8px' }}>
              <Input placeholder="téléphone déposant" autoComplete="on" />
            </Form.Item>
          </Form.Item>

          <Form.Item label="Itinéraire" style={{ marginBottom: 0 }}>
            <Form.Item
                name='pays_depart'
                rules={[{ required: true, message: 'Lieu de départ requis' }]}
                style={{ display: 'inline-block', width: 'calc(50% - 8px)' }}
            >
              <Select showSearch placeholder="Lieu de départ" optionFilterProp="children"
                      filterOption={(input, option: any) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
              >
                {ITINERAIRES_LIST.map(itineraire => (
                    <Option key={itineraire.code} value={itineraire.code}>{itineraire.nom}</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
                name='pays_recuperation'
                rules={[{ required: true, message: 'Lieu de récupération requis' }]}
                style={{ display: 'inline-block', width: 'calc(50% - 8px)', margin: '0 8px' }}
            >
              <Select showSearch placeholder="Lieu de récupération" optionFilterProp="children"
                      filterOption={(input, option: any) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
              >
                {ITINERAIRES_LIST.map(itineraire => (
                    <Option key={itineraire.code} value={itineraire.code}>{itineraire.nom}</Option>
                ))}
              </Select>
            </Form.Item>
          </Form.Item>

          <Form.Item name='type_paiement' label="Type de paiement"
                     rules={[{ required: true, message: 'Veuillez sélectionner le type de paiement' }]}
          >
            <Select placeholder="Sélectionnez le type de paiement">
              <Option value="espece">Espèce</Option>
              <Option value="virement">Virement</Option>
              <Option value="depot_mobile">Dépôt mobile</Option>
              <Option value="cheque">Chèque</Option>
            </Select>
          </Form.Item>

          <Form.Item name='motif' label="Motif"
                     rules={[{ required: true, message: 'Veuillez sélectionner le motif' }]}
          >
            <Select placeholder="Sélectionnez le motif">
              <Option value="achat">Achat</Option>
              <Option value="vente">Vente</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Devises" style={{ marginBottom: 0 }}>
            <Form.Item name='devise_id' rules={[{ required: true }]}
                       style={{ display: 'inline-block', width: 'calc(50% - 8px)' }}
            >
              <SelectSearchInput onChange={(v) => onDeviseChange('devise_id', v)} onSearch={(value) => onSearch(value)}
                                 label='code' valueLabel='code' data={devises} placeholder="Devise d'entrée"
              />
            </Form.Item>
            <Form.Item name='devise_sortie' rules={[{ required: true }]}
                       style={{ display: 'inline-block', width: 'calc(50% - 8px)', margin: '0 8px' }}
            >
              <SelectSearchInput onChange={(v) => onDeviseChange('devise_sortie', v)} onSearch={(value) => onSearch(value)}
                                 label='code' valueLabel='code' data={devises} placeholder="Devise de sortie"
              />
            </Form.Item>
          </Form.Item>

          <Form.Item name='taux' label="Le taux"
                     rules={[{ required: true, message: 'Le taux est requis avant de saisir le montant' }]}
          >
            <InputNumber
                onChange={onTauxChange}
                placeholder="Saisissez le taux d'abord"
                style={{ width: '100%' }}
                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value: any) => value.replace(/\$\s?|(,*)/g, '')}
            />
          </Form.Item>

          <Form.Item name='montant' label="Montant" rules={[{ type: 'number', required: true }]}
                     tooltip={!canEnterMontant ? "Veuillez d'abord saisir les devises et le taux" : ""}
          >
            <InputNumber
                disabled={!canEnterMontant}
                onChange={onMontantChange}
                placeholder={canEnterMontant ? "Montant" : "Saisissez d'abord les devises et le taux"}
                style={{ width: '100%' }}
                formatter={value => {
                  if (!value) return '';
                  return Math.floor(Number(value)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                }}
                parser={(value: any) => value.replace(/,/g, '')}
            />
          </Form.Item>

          <Form.Item name='montant_convert' label="M.Converti">
            <InputNumber
                disabled
                placeholder="Montant converti"
                style={{ width: '100%' }}
                formatter={value => {
                  if (!value) return '';
                  return Math.floor(Number(value)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                }}
                parser={(value: any) => value.replace(/,/g, '')}
            />
          </Form.Item>

          {isVipClient && (
              <Form.Item
                  label={
                    <span>
        Ajustement VIP
        <Tooltip title={
          <div>
            <p><strong>+ Crédit :</strong> Le client récupère plus que son dépôt. Il nous devra la différence.</p>
            <p><strong>− Réserve :</strong> Le client ne prend qu'une partie. Le reste est gardé sur son compte.</p>
            <p><strong>0 :</strong> Pas d'ajustement, seuls les soldes existants s'appliquent.</p>
          </div>
        }>
          <InfoCircleOutlined style={{ marginLeft: 4, color: '#1890ff' }} />
        </Tooltip>
      </span>
                  }
              >
                <div>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                    <Button
                        icon={<PlusOutlined />}
                        size="small"
                        type={suppSign === 1 ? 'primary' : 'default'}
                        onClick={() => handleSignChange(1)}
                        style={{ flex: 1, fontSize: 11, padding: '0 4px', overflow: 'hidden' }}
                    >
                      Crédit
                    </Button>
                    <Button
                        icon={<MinusOutlined />}
                        size="small"
                        danger={suppSign === -1}
                        type={suppSign === -1 ? 'primary' : 'default'}
                        onClick={() => handleSignChange(-1)}
                        style={{ flex: 1, fontSize: 11, padding: '0 4px', overflow: 'hidden' }}
                    >
                      Réserve
                    </Button>
                  </div>

                  <Form.Item name='montant_supplementaire' noStyle>
                    <Input.Group compact style={{ display: 'flex', marginBottom: 8 }}>
                      <Input
                          disabled
                          value={suppSign === 1 ? '+' : '−'}
                          style={{
                            width: 36,
                            textAlign: 'center',
                            fontWeight: 'bold',
                            fontSize: 16,
                            color: suppSign === 1 ? '#52c41a' : '#ff4d4f',
                            backgroundColor: suppSign === 1 ? '#f6ffed' : '#fff2f0',
                            borderColor: suppSign === 1 ? '#b7eb8f' : '#ffccc7',
                            borderRight: 'none',
                            cursor: 'default',
                          }}
                      />

                      <InputNumber
                          style={{ flex: 1 }}
                          min={0}
                          placeholder="0 = pas d'ajustement"
                          value={Math.abs(Number(form.getFieldValue('montant_supplementaire')) || 0)}
                          onChange={(value) => {
                            const absVal = Number(value) || 0;
                            setSuppAbsValue(absVal);
                            const newVal = absVal * suppSign;
                            form.setFieldsValue({ montant_supplementaire: newVal });
                            onMontantSupplementaireChange(newVal);
                          }}
                          formatter={value => {
                            if (!value && value !== 0) return '';
                            return Math.floor(Number(value)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                          }}
                          parser={(value: any) => value.replace(/,/g, '')}
                      />
                    </Input.Group>
                  </Form.Item>

                  <div style={{ marginTop: 6 }}>
                    {suppSign === 1 ? (
                        <Text type="success" style={{ fontSize: 11 }}>
                          <ArrowUpOutlined /> Récupère <strong>plus</strong> que son dépôt.
                        </Text>
                    ) : (
                        <Text type="warning" style={{ fontSize: 11 }}>
                          <ArrowDownOutlined /> Récupère <strong>moins</strong> que son dépôt.
                        </Text>
                    )}
                  </div>
                </div>
              </Form.Item>
          )}

          <Form.Item initialValue='depot' name='type_operation' noStyle>
            <Input type="hidden" />
          </Form.Item>

        </Form>
      </Modal>
  );
};

export default CollectionCreateForms;